import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { dataProvider } from './dataProvider.js';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { SnackbarProvider } from 'notistack';
import moment from 'moment-timezone';
import './i18n'; // Importeer i18n configuratie
import { AuthProvider } from './contexts/AuthContext';
import { HealthProvider } from './contexts/HealthContext';
import MultiTenantApp from './components/MultiTenantApp';

// Configureer moment.js voor Nederlandse locale en tijdzone
// Standaard gebruiken we CET (Central European Time) voor de frontend
// Alle datums worden intern in UTC opgeslagen, maar getoond in CET
moment.locale('nl');
moment.tz.setDefault('Europe/Amsterdam');

// Eenvoudige i18n provider voor React Admin
const i18nProvider = {
    translate: (key, options) => {
        // Gebruik de huidige taal uit localStorage
        const currentLanguage = localStorage.getItem('language') || 'nl';
        
        // Importeer de juiste vertaling
        let messages;
        try {
            messages = currentLanguage === 'en' 
                ? require('./i18n/en.json') 
                : require('./i18n/nl.json');
        } catch (error) {
            // Fallback naar Nederlandse vertaling
            messages = require('./i18n/nl.json');
        }
        
        // Zoek de vertaling in de messages
        const keys = key.split('.');
        let value = messages;
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                return key; // Fallback naar de originele key
            }
        }
        
        // Vervang placeholders als die er zijn
        if (options && typeof value === 'string') {
            Object.keys(options).forEach(optionKey => {
                value = value.replace(new RegExp(`%{${optionKey}}`, 'g'), options[optionKey]);
            });
        }
        
        return value || key;
    },
    changeLocale: (locale) => {
        localStorage.setItem('language', locale);
        // Forceer een page refresh om de wijziging toe te passen
        window.location.reload();
    },
    getLocale: () => localStorage.getItem('language') || 'nl'
};

// Basis thema configuratie voor de applicatie
const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

/**
 * Hoofdcomponent van de applicatie met BrowserRouter wrapper
 * Alle routing gebeurt binnen deze context
 */
const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <BrowserRouter>
          <AuthProvider>
            <HealthProvider>
              <Routes>
              {/* Public routes - geen auth vereist */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              
              {/* Alle andere routes via MultiTenantApp */}
              <Route path="/*" element={
                <MultiTenantApp
                  dataProvider={dataProvider}
                  i18nProvider={i18nProvider}
                  theme={theme}
                />
              } />
            </Routes>
            </HealthProvider>
          </AuthProvider>
        </BrowserRouter>
      </SnackbarProvider>
    </LocalizationProvider>
  </ThemeProvider>
);

export default App; 